import React, { useState, useEffect, useRef } from 'react';
import { FaUser, FaEnvelope, FaShieldAlt, FaCalendarAlt, FaCog, FaEdit, FaSave, FaTimes, FaCamera } from 'react-icons/fa';
import './AdminComponents.css';
import './AdminProfile.css';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import { useNavigate } from 'react-router-dom';
import ChangePasswordModal from './ChangePasswordModal';
import AdminTwoFactorAuthManager from '../TwoFactorAuth/AdminTwoFactorAuthManager';

interface AdminProfileProps {
  adminUser: any;
  onProfileUpdate?: () => void;
  setActiveTab?: (tabId: string) => void;
}

const AdminProfile: React.FC<AdminProfileProps> = ({ adminUser, onProfileUpdate, setActiveTab }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [avatarUpdateKey, setAvatarUpdateKey] = useState(0);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [editedProfile, setEditedProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    department: ''
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const adminApi = useAdminApiService();
  const navigate = useNavigate();

  useEffect(() => {
    loadProfileData();
  }, []);

  // Remove the problematic useEffect and instead use adminUser prop directly
  const loadProfileData = async () => {
    // If we have adminUser prop with avatar, use it
    if (adminUser && adminUser.avatarBase64) {
      setProfileData(adminUser);
      setEditedProfile({
        firstName: adminUser.firstName || '',
        lastName: adminUser.lastName || '',
        email: adminUser.email || '',
        department: adminUser.department || ''
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getAdminProfile();
      
      if (response.success && response.data) {
        setProfileData(response.data);
        setEditedProfile({
          firstName: response.data.firstName || '',
          lastName: response.data.lastName || '',
          email: response.data.email || '',
          department: response.data.department || ''
        });
      } else {
        setError(response.error || 'Failed to load profile data');
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const response = await adminApi.uploadAdminAvatar(file);
      
      if (response.success && response.data) {
        // Update the profile data with the new avatar
        console.log('Upload successful, new profile data:', response.data);
        setProfileData(response.data);
        setAvatarUpdateKey(prev => prev + 1); // Force re-render
        // Call the callback to refresh admin user data in parent component
        onProfileUpdate?.();
      } else {
        alert(response.error || 'Failed to upload profile picture');
      }
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      alert('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    // TODO: Implement save functionality
    console.log('Saving profile:', editedProfile);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedProfile({
      firstName: profileData?.firstName || '',
      lastName: profileData?.lastName || '',
      email: profileData?.email || '',
      department: profileData?.department || ''
    });
    setIsEditing(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async (oldPassword: string, newPassword: string) => {
    const response = await adminApi.changeAdminPassword(oldPassword, newPassword);
    if (!response.success) {
      throw new Error(response.error || 'Failed to change password');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPermissions = (permissions: string[]) => {
    if (!permissions || permissions.length === 0) return 'No specific permissions';
    return permissions.join(', ');
  };

  if (loading) {
    return (
      <div className="admin-profile-page">
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading profile data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-profile-page">
        <div className="profile-error">
          <p>Error: {error}</p>
          <button onClick={loadProfileData} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="admin-profile-page">
        <div className="profile-error">
          <p>No profile data available</p>
        </div>
      </div>
    );
  }

  console.log('Rendering AdminProfile with profileData:', profileData);

  return (
    <div className="admin-profile-page">
      <div className="profile-header">
        <div className="profile-avatar-container" key={avatarUpdateKey}>
          <div 
            className={`profile-avatar-large ${uploading ? 'uploading' : ''}`}
            onClick={handleAvatarClick}
            title="Click to upload new profile picture"
          >
            {profileData?.avatarBase64 ? (
              <img 
                src={`data:image/jpeg;base64,${profileData.avatarBase64}`}
                alt="Profile"
                className="avatar-image"
                onLoad={() => console.log('Avatar image loaded successfully')}
                onError={(e) => console.error('Avatar image failed to load:', e)}
              />
            ) : (
              <>
                <FaUser />
                <span className="avatar-initial">
                  {profileData?.fullName?.charAt(0) || 'A'}
                </span>
              </>
            )}
            <div className="avatar-upload-overlay">
              <div style={{ textAlign: 'center' }}>
                <FaCamera />
                <div style={{ fontSize: '10px', marginTop: '4px' }}>Upload</div>
              </div>
            </div>
            {uploading && (
              <div className="avatar-upload-loading">
                <div className="upload-spinner"></div>
                <span>Uploading...</span>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleAvatarChange}
            disabled={uploading}
          />
        </div>
        <div className="profile-header-info">
          <h1>{profileData?.fullName || 'Admin User'}</h1>
          <p className="profile-role">{profileData?.role || 'ADMIN'}</p>
          <p className="profile-department">{profileData?.department || 'Administration'}</p>
        </div>
        <div className="profile-actions">
          {!isEditing ? (
            <button className="edit-profile-btn" onClick={handleEdit}>
              <FaEdit /> Edit Profile
            </button>
          ) : (
            <div className="edit-actions">
              <button className="save-btn" onClick={handleSave}>
                <FaSave /> Save
              </button>
              <button className="cancel-btn" onClick={handleCancel}>
                <FaTimes /> Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="profile-content">
        <div className="profile-section">
          <h2>Personal Information</h2>
          <div className="profile-grid">
            <div className="profile-field">
              <label>First Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedProfile.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="profile-input"
                />
              ) : (
                <span>{profileData?.firstName || 'N/A'}</span>
              )}
            </div>
            <div className="profile-field">
              <label>Last Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedProfile.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="profile-input"
                />
              ) : (
                <span>{profileData?.lastName || 'N/A'}</span>
              )}
            </div>
            <div className="profile-field">
              <label>Email</label>
              {isEditing ? (
                <input
                  type="email"
                  value={editedProfile.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="profile-input"
                />
              ) : (
                <span>{profileData?.email || 'N/A'}</span>
              )}
            </div>
            <div className="profile-field">
              <label>Department</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedProfile.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  className="profile-input"
                />
              ) : (
                <span>{profileData?.department || 'N/A'}</span>
              )}
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h2>Account Information</h2>
          <div className="profile-grid">
            <div className="profile-field">
              <label>Username</label>
              <span>{profileData?.username || 'N/A'}</span>
            </div>
            <div className="profile-field">
              <label>Role</label>
              <span className="role-badge">{profileData?.role || 'ADMIN'}</span>
            </div>
            <div className="profile-field">
              <label>Account Created</label>
              <span>{formatDate(profileData?.createdAt)}</span>
            </div>
            <div className="profile-field">
              <label>Last Login</label>
              <span>{formatDate(profileData?.lastLoginAt)}</span>
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h2>Permissions</h2>
          <div className="permissions-list">
            {profileData?.permissions && profileData.permissions.length > 0 ? (
              <div className="permissions-grid">
                {profileData.permissions.map((permission: string, index: number) => (
                  <span key={index} className="permission-badge">
                    {permission}
                  </span>
                ))}
              </div>
            ) : (
              <p className="no-permissions">No specific permissions assigned</p>
            )}
          </div>
        </div>

        <div className="profile-section">
          <h2>Account Settings</h2>
          <div className="settings-grid">
            <div className="setting-item">
              <div className="setting-info">
                <h3>Change Password</h3>
                <p>Update your account password</p>
              </div>
              <button className="setting-btn" onClick={() => setShowChangePasswordModal(true)}>Change Password</button>
            </div>
            <div className="setting-item" style={{ gridColumn: '1 / -1' }}>
              <div className="setting-info">
                <h3>Two-Factor Authentication</h3>
                <p>Enable additional security for your account</p>
              </div>
              <div style={{ marginTop: '16px' }}>
                <AdminTwoFactorAuthManager />
              </div>
            </div>
            <div className="setting-item">
              <div className="setting-info">
                <h3>Session Management</h3>
                <p>View and manage active sessions</p>
              </div>
              <button className="setting-btn" onClick={() => setActiveTab ? setActiveTab('adminSettings') : undefined}>Manage Sessions</button>
            </div>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onChangePassword={handleChangePassword}
      />
    </div>
  );
};

export default AdminProfile; 